<template>
    <charges-find
        :params="{ charge: $route.params.charge }"
        :include="[
            'fees(include:property)',
            'rents(include:property)'
        ]"
    >
        <div
            slot-scope="{
                sid,
                data: charge,
                error,
                is_loading,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || error">
                <base-loading
                    :error="error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="error"
                    :error="error"
                />
            </template>

            <template v-else>
                <grid-layout>
                    <grid-layout-item
                        v-if="charge.state === 'unconfirmed'"
                        class="mb-6"
                    >
                        <with-dimensions
                            v-slot="{
                                width
                            }"
                        >
                            <div class="relative bg-white rounded shadow pt-1-5">
                                <div class="absolute inset-x-0 top-0 rounded-t bg-red h-1-5" />

                                <div
                                    :class="width > 500 ? 'p-6' : 'p-3'"
                                    class="flex w-full border-b"
                                >
                                    <base-svg
                                        name="icon-important-circle-large"
                                        :class="width > 500 ? 'w-8 h-8 mr-4' : 'w-6 h-6 mr-3'"
                                        class="flex-none fill-primary-red-dark fill-secondary-red-light"
                                    />

                                    <div class="flex-grow">
                                        <h1
                                            :class="width > 500 ? 'py-1' : ''"
                                            class="flex-grow mb-3 text-lg font-normal"
                                        >
                                            Unconfirmed Payment
                                        </h1>

                                        <p>This Smart Payment requires further action. You must manually confirm this payment.</p>
                                    </div>
                                </div>

                                <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.charges.confirm'),
                                            params: {
                                                charge: charge.uuid
                                            }
                                        }"
                                        :class="width > 500 ? 'w-auto' : 'w-full'"
                                        class="btn btn-red"
                                    >
                                        Confirm Payment
                                    </router-link>
                                </div>
                            </div>
                        </with-dimensions>
                    </grid-layout-item>

                    <grid-layout-item class="mb-6 md:w-2/3">
                        <details-card class="md:h-full">
                            <template slot="header">
                                <h1 class="text-lg font-normal">
                                    Payment
                                </h1>

                                <div
                                    v-if="is_loading"
                                    class="w-6 h-6 mr-3"
                                >
                                    <base-svg
                                        name="icon-loading"
                                        class="w-full h-full"
                                    />
                                </div>
                            </template>

                            <template slot="body">
                                <grid-layout>
                                    <grid-layout-item class="mb-3 sm:w-1/2 lg:w-1/3">
                                        <div class="text-xs text-label">Paid</div>
                                        <div class="text-sm">
                                            {{ charge.created_at | moment('D MMM YYYY') }}
                                        </div>
                                    </grid-layout-item>

                                    <grid-layout-item
                                        v-if="charge.is_offline"
                                        class="mb-3 sm:w-1/2 lg:w-1/3"
                                    >
                                        <div class="text-xs text-label">Note</div>
                                        <div class="text-sm">
                                            {{ charge.note || 'n/a' }}
                                        </div>
                                    </grid-layout-item>
                                </grid-layout>
                            </template>
                        </details-card>
                    </grid-layout-item>

                    <grid-layout-item class="mb-6 md:w-1/3">
                        <div class="flex flex-col h-full bg-white rounded shadow">
                            <header class="flex items-center flex-none h-16 px-4 border-b md:h-18 sm:px-6">
                                <h3 class="flex-grow text-base font-normal">Amount</h3>

                                <span
                                    :class="{
                                        'text-grey-darker bg-grey-lighter': charge.state === 'requested' || charge.state === 'pending',
                                        'text-green-darker bg-green-lighter': charge.state === 'succeeded',
                                        'text-red-darker bg-red-lighter': charge.state === 'failed',
                                        'text-yellow-darker bg-yellow-lighter': charge.state ==='unconfirmed',
                                    }"
                                    class="px-3 py-1 text-sm font-semibold rounded-full"
                                >
                                    {{ charge.state }}
                                </span>
                            </header>

                            <div class="flex items-center flex-grow p-4 leading-none sm:p-6">
                                <div class="flex-none">
                                    <div
                                        :class="{
                                            'line-through': charge.state === 'failed'
                                        }"
                                        class="text-3xl font-semibold"
                                    >
                                        {{ charge.amount | money(0) }}
                                    </div>
                                    <div class="text-sm text-grey-darker">
                                        <template v-if="charge.is_offline">Offline</template>
                                        <template v-else-if="charge.was_scheduled">Smart Payment</template>
                                        <template v-else>Card</template>
                                    </div>
                                </div>
                            </div>

                            <charges-acknowledge-edit-button
                                v-if="$gate.role('landlord') && charge.state === 'requested'"
                                :charge="charge"
                                @success="find"
                            />
                        </div>
                    </grid-layout-item>
                </grid-layout>

                <payment-summary-card
                    :rents="charge.rents.data"
                    :fees="charge.fees.data"
                />
            </template>
        </div>
    </charges-find>
</template>

<script>
    import ChargesAcknowledgeEditButton from '../../landlords/charges/show/charges-acknowledge-edit-button'
    import ChargesFind from '../../../../components/charges-find'
    import DetailsCard from '../../../../components/details-card'
    import PaymentSummaryCard from '../../../../components/payment-summary-card'

    export default {
        components: {
            ChargesAcknowledgeEditButton,
            ChargesFind,
            DetailsCard,
            PaymentSummaryCard
        }
    }
</script>
